import { Contact, ContactInput, ContactsResponse, Category, CategoryInput, CategoriesResponse, ImportResult, BulkCategoryUpdateResponse } from '../types';

const rawBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
export const API_BASE_URL = rawBaseUrl.endsWith('/') ? rawBaseUrl.slice(0, -1) : rawBaseUrl;

export const buildApiUrl = (endpoint: string): string => {
  if (!endpoint) return API_BASE_URL;
  const hasProtocol = /^https?:\/\//i.test(endpoint);
  if (hasProtocol) return endpoint;
  const normalized = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_BASE_URL}${normalized}`;
};

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = buildApiUrl(endpoint);
    const token = localStorage.getItem('auth_token');

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      cache: 'no-store',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      const detailsMessage =
        typeof error.details === 'string'
          ? error.details
          : Array.isArray(error.details) && error.details.length > 0
            ? error.details.map((item: any) => item?.msg || item?.message || '').filter(Boolean).join('; ')
            : '';

      // Detectar erro de quota e adicionar flag para tratamento especial
      if (error.upgradeRequired || (error.message && error.message.includes('Limite'))) {
        const quotaError = new Error(error.message || error.error || 'Limite atingido');
        (quotaError as any).isQuotaError = true;
        (quotaError as any).upgradeRequired = true;
        throw quotaError;
      }

      const statusText = typeof error.status === 'string' ? error.status.toUpperCase() : '';
      const isPaymentRequired =
        response.status === 402 ||
        error.paymentRequired ||
        error.requiresPayment ||
        statusText === 'PAYMENT_REQUIRED' ||
        statusText === 'REQUIRES_PAYMENT' ||
        statusText === 'PAST_DUE' ||
        statusText === 'EXPIRED';

      if (isPaymentRequired) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('app:payment-required', { detail: error }));
        }
        const paymentError = new Error(error.error || error.message || 'Pagamento necessário');
        (paymentError as any).paymentRequired = true;
        (paymentError as any).paymentNotice = error;
        throw paymentError;
      }

      throw new Error(detailsMessage || error.error || error.message || `HTTP ${response.status}`);
    }

    // Handle empty responses (like 204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }

    return response.json();
  }

  async getContacts(params?: {
    search?: string;
    tag?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ContactsResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.tag) searchParams.set('tag', params.tag);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/contatos${queryString ? `?${queryString}` : ''}`;

    return this.request<ContactsResponse>(endpoint);
  }

  async getContact(id: string): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`);
  }

  async createContact(data: ContactInput): Promise<Contact> {
    return this.request<Contact>(`/contatos`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContact(id: string, data: ContactInput): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteContact(id: string): Promise<void> {
    await this.request<void>(`/contatos/${id}`, {
      method: 'DELETE',
    });
  }

  async updateContactsCategory(contactIds: string[], categoriaId: string | null): Promise<BulkCategoryUpdateResponse> {
    return this.request<BulkCategoryUpdateResponse>('/contatos/categoria/em-lote', {
      method: 'POST',
      body: JSON.stringify({
        contactIds,
        categoriaId: categoriaId || null,
      }),
    });
  }

  // Category methods
  async getCategories(params?: {
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<CategoriesResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/categorias${queryString ? `?${queryString}` : ''}`;

    return this.request<CategoriesResponse>(endpoint);
  }

  async getAllCategories(): Promise<Category[]> {
    return this.request<Category[]>(`/categorias/all`);
  }

  async getCategory(id: string): Promise<Category> {
    return this.request<Category>(`/categorias/${id}`);
  }

  async createCategory(data: CategoryInput): Promise<Category> {
    return this.request<Category>(`/categorias`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(id: string, data: CategoryInput): Promise<Category> {
    return this.request<Category>(`/categorias/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(id: string): Promise<void> {
    await this.request<void>(`/categorias/${id}`, {
      method: 'DELETE',
    });
  }

  // CSV Import methods
  async importCSV(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('csv', file);

    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    const response = await fetch(`${API_BASE_URL}/csv/import`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async downloadCSVTemplate(): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    const response = await fetch(`${API_BASE_URL}/csv/template`, {
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.blob();
  }

  async importCertificationCSV(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('csv', file);

    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    const response = await fetch(`${API_BASE_URL}/csv/certification/import`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async readMandatePdfWithAi(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    const response = await fetch(buildApiUrl('/mandates/read-pdf'), {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      throw new Error(error.message || error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async listMandates(params?: {
    search?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<any> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    return this.request(`/mandates${queryString ? `?${queryString}` : ''}`);
  }

  async listCampaigns(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<any> {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.search) searchParams.set('search', params.search);

    const queryString = searchParams.toString();
    return this.request(`/campaigns${queryString ? `?${queryString}` : ''}`);
  }

  async getCampaignSessions(): Promise<any> {
    return this.request('/campaigns/sessions');
  }

  async createCampaignFromMandates(data: {
    nome: string;
    mandateIds: string[];
    sessionNames: string[];
    messageType: string;
    messageContent: { text: string };
    randomDelay?: number;
    startImmediately?: boolean;
    scheduledFor?: string | null;
  }): Promise<any> {
    return this.request('/campaigns/from-mandates', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async createMandate(data: {
    arquivoPdf?: string | null;
    dadosExtraidos?: any;
    nomeDestinatario?: string | null;
    telefone?: string | null;
    endereco?: string | null;
    status?: string;
    certificacaoTexto?: string | null;
  }): Promise<any> {
    return this.request('/mandates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMandate(id: string, data: {
    dadosExtraidos?: any;
    nomeDestinatario?: string | null;
    telefone?: string | null;
    endereco?: string | null;
    status?: string;
    certificacaoTexto?: string | null;
  }): Promise<any> {
    return this.request(`/mandates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createMandateFromPdf(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    const response = await fetch(buildApiUrl('/mandates/from-pdf'), {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
      throw new Error(error.message || error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async downloadCertificationCSVTemplate(): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    const response = await fetch(`${API_BASE_URL}/csv/certification/template`, {
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.blob();
  }
}

export const apiService = new ApiService();
